import { useEffect, useRef } from 'react';

type Opts = {
  intervalMs?: number;
  enabled?: boolean;
  ignoreDuringPaths?: string[]; // ścieżki, na których NIE robimy reloadu
};

const isIgnored = (paths: string[]) => {
  const p = window.location.pathname;
  return paths.some((pref) => p.startsWith(pref));
};

export function useAutoReloadOnNewVersion({
  intervalMs = 30000,
  enabled = true,
  ignoreDuringPaths = ['/checkout', '/payment'],
}: Opts = {}) {
  const current = useRef<string | null>(null);
  const timer = useRef<number | null>(null);
  const pendingReload = useRef(false);

  useEffect(() => {
    if (!enabled) return;

    const check = async () => {
      try {
        const res = await fetch('/version.json', { cache: 'no-store' });
        if (!res.ok) return;
        const { version } = await res.json();

        if (current.current === null) {
          current.current = version;
        } else if (version && version !== current.current) {
          // jeśli user jest na checkout, czekamy aż wyjdzie
          if (isIgnored(ignoreDuringPaths)) {
            pendingReload.current = true;
            return;
          }
          window.location.reload();
        }
      } catch {}
    };

    const maybeReloadPending = () => {
      if (pendingReload.current && !isIgnored(ignoreDuringPaths)) {
        window.location.reload();
      }
    };

    check();
    timer.current = window.setInterval(check, intervalMs);

    window.addEventListener('focus', maybeReloadPending);
    document.addEventListener('visibilitychange', maybeReloadPending);

    return () => {
      if (timer.current) window.clearInterval(timer.current);
      window.removeEventListener('focus', maybeReloadPending);
      document.removeEventListener('visibilitychange', maybeReloadPending);
    };
  }, [enabled, intervalMs, ignoreDuringPaths]);
}